import type { AreaId } from './world';

// ─────────────────────────────────────────────────────────────────────────────
// Sea beasts of the lower levels: sharks, eels, jellies and anglerfish in the
// caves and the deep; sea serpents and giant crabs in the Sunken Temples; the
// Kraken in the Abyss. Shared by everyone in the same part of the map (see the
// sea-beasts section of engine.ts). Here are
// what each kind is like, where it lives, and how it's drawn (in world units,
// facing right when facing = 1, in the same ink outline as everything else).
// ─────────────────────────────────────────────────────────────────────────────

export type BeastKind = 'shark' | 'eel' | 'jelly' | 'angler' | 'serpent' | 'crab' | 'kraken';

export type Beast = {
  id: string; kind: BeastKind;
  x: number; y: number; vx: number; vy: number;
  hp: number; facing: 1 | -1;
  /** Starting health when it differs from the kind's (the Kraken grows with the class). */
  max?: number;
  /** Seconds until it can bite again (it backs off after a bite). */
  cool: number;
  /** Seconds left of the flash after being hit. */
  hurt: number;
  /** Eels: where they lurk, and how long a lunge has left. */
  home: { x: number; y: number }; lunge: number;
  turn: number; seed: number;
  /** Following someone else's beasts: where they last said it was, and when. */
  tx?: number; ty?: number; at?: number;
};

/** A beast as sent to classmates (rounded, short keys). */
export type BeastWire = { id: string; k: BeastKind; x: number; y: number; vx: number; vy: number; hp: number; f: 1 | -1; c: number; l: number; s: number; m?: number };
/** The beasts of one region of the map, from the classmate running them. */
export type BeastsMsg = { r: number; from: string; list: BeastWire[]; dead?: { id: string; x: number; y: number; by: string | null; k: BeastKind }[] };
/** Something that happened to a beast on a follower's screen, for the one running them. */
export type BeastEvent = { t: 'hit' | 'bite'; id: string; by: string; dmg?: number; fx?: 1 | -1 };

export const toWire = (b: Beast): BeastWire => ({
  id: b.id, k: b.kind, x: Math.round(b.x), y: Math.round(b.y), vx: Math.round(b.vx), vy: Math.round(b.vy),
  hp: b.hp, f: b.facing, c: Math.round(b.cool * 10) / 10, l: Math.round(b.lunge * 10) / 10, s: Math.round(b.seed), ...(b.max ? { m: b.max } : {}),
});

type Def = {
  name: string; r: number; hp: number;
  wander: number; chase: number; aggro: number;
  /** Where it can appear. */
  lives: (area: AreaId, y: number) => boolean;
  weight: number;
  /** Only one at a time in a part of the map (the Kraken). */
  unique?: boolean;
  /** Walks the floor instead of swimming (crabs). */
  walks?: boolean;
};

const CAVES = new Set<AreaId>(['caves', 'grotto', 'echo', 'hollow']);
const TEMPLE = new Set<AreaId>(['temple', 'abyss']);

/** Nothing lives above this: the shallows and the upper sea are safe. */
export const BEAST_LINE = 1250;

export const BEASTS: Record<BeastKind, Def> = {
  shark: { name: 'shark', r: 34, hp: 3, wander: 80, chase: 235, aggro: 430, weight: 3, lives: (a, y) => !CAVES.has(a) && !TEMPLE.has(a) && (a === 'trench' || y > 1800) },
  eel: { name: 'moray eel', r: 22, hp: 2, wander: 25, chase: 400, aggro: 210, weight: 3, lives: a => CAVES.has(a) || a === 'temple' },
  jelly: { name: 'stinging jelly', r: 22, hp: 1, wander: 30, chase: 30, aggro: 0, weight: 2, lives: (_a, y) => y > BEAST_LINE + 100 },
  angler: { name: 'anglerfish', r: 30, hp: 4, wander: 50, chase: 150, aggro: 520, weight: 2, lives: (a, y) => (a === 'trench' && y > 2300) || a === 'hollow' || TEMPLE.has(a) },
  // The Sunken Temples: tougher, faster, and more of them.
  // A little gentler than at first (was 6 hp, chase 300, aggro 540, weight 4): it nearly matched your top speed.
  serpent: { name: 'sea serpent', r: 28, hp: 4, wander: 80, chase: 250, aggro: 440, weight: 3, lives: a => a === 'temple' },
  crab: { name: 'giant crab', r: 36, hp: 5, wander: 40, chase: 170, aggro: 380, weight: 3, walks: true, lives: (a, y) => a === 'temple' || (CAVES.has(a) && y > 3000) },
  kraken: { name: 'Kraken', r: 82, hp: 20, wander: 40, chase: 120, aggro: 700, weight: 6, unique: true, lives: a => a === 'abyss' },
};

/** Pick a kind that lives here, or null. */
export function beastFor(area: AreaId, y: number, rand: () => number): BeastKind | null {
  if (y < BEAST_LINE) return null;
  const ok = (Object.keys(BEASTS) as BeastKind[]).filter(k => BEASTS[k].lives(area, y));
  const total = ok.reduce((s, k) => s + BEASTS[k].weight, 0);
  let r = rand() * total;
  for (const k of ok) { r -= BEASTS[k].weight; if (r <= 0) return k; }
  return null;
}

const INK = '#1e2633';

/** Draw a beast; `lights` gets glowing bits (jellies, the angler's lure). */
export function drawBeast(g: CanvasRenderingContext2D, b: Beast, t: number, lights: [number, number, number, string][]) {
  g.save();
  g.translate(b.x, b.y);
  g.scale(b.facing, 1);
  g.lineJoin = 'round'; g.lineCap = 'round'; g.strokeStyle = INK; g.lineWidth = 3;
  const sw = Math.sin(t * 6 + b.seed);
  switch (b.kind) {
    case 'shark': {
      const bite = b.cool <= 0 && Math.hypot(b.vx, b.vy) > 160;
      // Tail.
      g.fillStyle = '#7f93a6';
      g.beginPath(); g.moveTo(-50, 0); g.lineTo(-78, -24 + sw * 4); g.lineTo(-70, 0); g.lineTo(-78, 22 + sw * 4); g.closePath(); g.fill(); g.stroke();
      // Dorsal and side fins.
      g.beginPath(); g.moveTo(-8, -20); g.lineTo(4, -46); g.lineTo(20, -18); g.closePath(); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(4, 10); g.lineTo(-10, 30); g.lineTo(18, 14); g.closePath(); g.fill(); g.stroke();
      // Body, belly.
      g.beginPath(); g.moveTo(-56, 0); g.bezierCurveTo(-40, -26, 30, -28, 62, -4); g.lineTo(bite ? 50 : 58, bite ? 8 : 4); g.bezierCurveTo(30, 22, -40, 20, -56, 0); g.closePath(); g.fill();
      g.fillStyle = '#e9eef2';
      g.beginPath(); g.moveTo(-40, 8); g.bezierCurveTo(-10, 20, 30, 18, 52, 6); g.bezierCurveTo(30, 10, -10, 12, -40, 8); g.fill();
      g.beginPath(); g.moveTo(-56, 0); g.bezierCurveTo(-40, -26, 30, -28, 62, -4); g.lineTo(bite ? 50 : 58, bite ? 8 : 4); g.bezierCurveTo(30, 22, -40, 20, -56, 0); g.closePath(); g.stroke();
      // Gills, eye, teeth.
      g.lineWidth = 1.6; for (const x of [16, 21, 26]) { g.beginPath(); g.moveTo(x, -6); g.lineTo(x - 2, 6); g.stroke(); }
      g.fillStyle = INK; g.beginPath(); g.arc(40, -8, 3.6, 0, Math.PI * 2); g.fill();
      g.strokeStyle = INK; g.lineWidth = 2; g.beginPath(); g.moveTo(34, -14); g.lineTo(46, -11); g.stroke();
      if (bite) { g.fillStyle = '#fff'; for (let i = 0; i < 4; i++) { g.beginPath(); g.moveTo(36 + i * 5, 3); g.lineTo(38 + i * 5, 8); g.lineTo(40 + i * 5, 3); g.fill(); } }
      break;
    }
    case 'eel': {
      // A long body waving behind the head.
      const pts: [number, number][] = [];
      for (let i = 0; i <= 10; i++) pts.push([30 - i * 11, Math.sin(t * 5 + b.seed - i * 0.7) * (3 + i * 1.6)]);
      g.strokeStyle = INK; g.lineWidth = 20;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.stroke();
      g.strokeStyle = '#6f8f3a'; g.lineWidth = 15;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.stroke();
      g.strokeStyle = '#d7e36a'; g.lineWidth = 2;
      for (let i = 1; i < 10; i += 2) { g.beginPath(); g.arc(pts[i][0], pts[i][1], 2.5, 0, Math.PI * 2); g.stroke(); }
      // Head.
      const open = b.lunge > 0;
      g.strokeStyle = INK; g.lineWidth = 3; g.fillStyle = '#7fa344';
      g.beginPath(); g.ellipse(36, 0, 18, 11, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      if (open) { g.fillStyle = '#5a1f2a'; g.beginPath(); g.moveTo(54, -2); g.lineTo(36, 2); g.lineTo(54, 9); g.closePath(); g.fill(); g.stroke(); g.fillStyle = '#fff'; for (const x of [42, 47]) { g.beginPath(); g.moveTo(x, 1); g.lineTo(x + 2, 6); g.lineTo(x + 4, 2); g.fill(); } }
      g.fillStyle = '#ffe066'; g.beginPath(); g.arc(42, -4, 3.6, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = INK; g.beginPath(); g.arc(43, -4, 1.6, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'jelly': {
      const pulse = Math.sin(t * 2.4 + b.seed);
      lights.push([b.x, b.y, 90, '#ff8fd8']);
      g.strokeStyle = 'rgba(255,140,210,0.75)'; g.lineWidth = 2.5;
      for (let i = 0; i < 6; i++) {
        const x = -14 + i * 5.6;
        g.beginPath(); g.moveTo(x, 4);
        for (let k = 1; k <= 6; k++) g.lineTo(x + Math.sin(t * 3 + i + k * 0.9) * 4, 4 + k * 8);
        g.stroke();
      }
      g.strokeStyle = INK; g.lineWidth = 2.5;
      g.fillStyle = 'rgba(255,160,220,0.75)';
      g.beginPath(); g.ellipse(0, 0, 22 + pulse * 2, 18 - pulse * 2, 0, Math.PI, 0); g.quadraticCurveTo(0, 10, -22 - pulse * 2, 0); g.fill(); g.stroke();
      g.fillStyle = 'rgba(255,255,255,0.6)'; g.beginPath(); g.ellipse(-7, -8, 5, 3, -0.4, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'angler': {
      // The lure, glowing, on its stalk.
      const lx = 44 + sw * 3, ly = -50 + Math.cos(t * 2 + b.seed) * 4;
      lights.push([b.x + lx * b.facing, b.y + ly, 210, '#b8fff3']);
      g.strokeStyle = INK; g.lineWidth = 2.5;
      g.beginPath(); g.moveTo(8, -26); g.quadraticCurveTo(30, -64, lx, ly); g.stroke();
      g.fillStyle = '#d9fffa'; g.beginPath(); g.arc(lx, ly, 7, 0, Math.PI * 2); g.fill(); g.stroke();
      // Tail and body.
      g.fillStyle = '#3d3550';
      g.beginPath(); g.moveTo(-34, 0); g.lineTo(-56, -18 + sw * 4); g.lineTo(-56, 18 + sw * 4); g.closePath(); g.fill(); g.stroke();
      g.beginPath(); g.arc(0, 0, 36, 0, Math.PI * 2); g.fill(); g.stroke();
      // The big mouth and teeth.
      g.fillStyle = '#1a0f1f';
      g.beginPath(); g.moveTo(36, -10); g.quadraticCurveTo(10, 4, 34, 22); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#fff';
      for (let i = 0; i < 4; i++) { g.beginPath(); g.moveTo(30 - i * 3, -6 + i * 6); g.lineTo(22 - i * 3, -2 + i * 6); g.lineTo(30 - i * 3, 0 + i * 6); g.fill(); }
      g.fillStyle = '#c8f7ff'; g.beginPath(); g.arc(14, -16, 6, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = INK; g.beginPath(); g.arc(16, -16, 2.6, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'serpent': {
      // A long sea serpent: a spiny back, a frilled head, and a body that waves.
      const pts: [number, number][] = [];
      for (let i = 0; i <= 14; i++) pts.push([40 - i * 13, Math.sin(t * 4 + b.seed - i * 0.6) * (4 + i * 2)]);
      g.strokeStyle = INK; g.lineWidth = 26;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.stroke();
      g.strokeStyle = '#2a8f8a'; g.lineWidth = 20;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y))); g.stroke();
      g.strokeStyle = '#9ff0c9'; g.lineWidth = 6;
      g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y + 6) : g.moveTo(x, y + 6))); g.stroke();
      g.fillStyle = '#c45cff'; g.strokeStyle = INK; g.lineWidth = 2.5;
      for (let i = 1; i < 13; i += 2) { const [x, y] = pts[i]; g.beginPath(); g.moveTo(x - 6, y - 9); g.lineTo(x, y - 24); g.lineTo(x + 6, y - 9); g.closePath(); g.fill(); g.stroke(); }
      g.lineWidth = 3;
      g.fillStyle = '#c45cff';
      g.beginPath(); g.moveTo(34, -10); g.lineTo(20, -40); g.lineTo(44, -18); g.lineTo(40, -44); g.lineTo(54, -14); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#2fa39d';
      g.beginPath(); g.ellipse(50, 0, 24, 15, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      if (b.cool <= 0) { g.fillStyle = '#5a1f2a'; g.beginPath(); g.moveTo(74, -3); g.lineTo(52, 3); g.lineTo(74, 11); g.closePath(); g.fill(); g.stroke(); }
      g.fillStyle = '#ffe066'; g.beginPath(); g.arc(58, -5, 4.5, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = INK; g.beginPath(); g.ellipse(59, -5, 1.4, 3.6, 0, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'crab': {
      // A giant armoured crab with snapping claws.
      const snap = b.cool <= 0 ? Math.abs(Math.sin(t * 8)) * 0.5 : 0.1;
      g.lineWidth = 7;
      for (const s2 of [-1, 1]) for (let i = 0; i < 3; i++) {
        const step = Math.sin(t * 9 + i + (s2 > 0 ? 1.5 : 0)) * 5;
        g.beginPath(); g.moveTo(s2 * 26, 6 + i * 6); g.lineTo(s2 * (48 + i * 6), 14 + i * 4 + step); g.lineTo(s2 * (56 + i * 8), 34); g.stroke();
      }
      g.lineWidth = 3;
      for (const s2 of [-1, 1]) {
        g.save(); g.translate(s2 * 46, -18);
        g.fillStyle = '#e8572a';
        g.beginPath(); g.ellipse(s2 * 16, -10, 20, 13, s2 * 0.4, 0, Math.PI * 2); g.fill(); g.stroke();
        g.save(); g.translate(s2 * 30, -16); g.rotate(-s2 * snap);
        g.beginPath(); g.moveTo(0, 0); g.lineTo(s2 * 18, -6); g.lineTo(s2 * 6, 4); g.closePath(); g.fill(); g.stroke(); g.restore();
        g.restore();
      }
      g.fillStyle = '#d9481f';
      g.beginPath(); g.ellipse(0, 0, 44, 28, 0, Math.PI, 0); g.quadraticCurveTo(0, 22, -44, 0); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#ff9a6b';
      for (const [x, y] of [[-18, -12], [0, -18], [18, -12]]) { g.beginPath(); g.arc(x, y, 5, 0, Math.PI * 2); g.fill(); }
      for (const s2 of [-1, 1]) {
        g.beginPath(); g.moveTo(s2 * 10, -24); g.lineTo(s2 * 13, -40); g.stroke();
        g.fillStyle = '#fff'; g.beginPath(); g.arc(s2 * 13, -44, 6, 0, Math.PI * 2); g.fill(); g.stroke();
        g.fillStyle = INK; g.beginPath(); g.arc(s2 * 13 + 1.5, -44, 2.6, 0, Math.PI * 2); g.fill();
      }
      break;
    }
    case 'kraken': {
      // The Kraken: a great mantle, huge eyes, and arms reaching out.
      lights.push([b.x, b.y, 160, '#ff6b8a']);
      g.lineWidth = 4;
      for (let i = 0; i < 8; i++) {
        const a0 = Math.PI * 0.15 + (i / 7) * Math.PI * 0.7;
        const reach = 120 + (i % 3) * 18;
        g.strokeStyle = INK; g.lineWidth = 20;
        g.beginPath(); g.moveTo(Math.cos(a0) * 30, 20);
        for (let k = 1; k <= 6; k++) {
          const f = k / 6, w = Math.sin(t * 2.2 + i + k * 0.8) * 22 * f;
          g.lineTo(Math.cos(a0) * (30 + reach * f) + w, 20 + Math.sin(a0) * reach * f);
        }
        g.stroke();
        g.strokeStyle = '#8f2b5a'; g.lineWidth = 13;
        g.stroke();
      }
      g.strokeStyle = INK; g.lineWidth = 4;
      g.fillStyle = '#a3366a';
      g.beginPath(); g.moveTo(-70, 30); g.bezierCurveTo(-90, -80, -20, -130, 30, -110); g.bezierCurveTo(90, -90, 96, -10, 70, 30); g.quadraticCurveTo(0, 50, -70, 30); g.closePath(); g.fill(); g.stroke();
      g.fillStyle = '#c45b8c';
      for (const [x, y, rr] of [[-30, -70, 10], [10, -90, 8], [40, -60, 12], [-10, -40, 7]]) { g.beginPath(); g.arc(x, y, rr, 0, Math.PI * 2); g.fill(); }
      for (const ex of [16, 50]) {
        g.fillStyle = '#ffe066'; g.beginPath(); g.ellipse(ex, 4, 14, 16, 0, 0, Math.PI * 2); g.fill(); g.stroke();
        g.fillStyle = INK; g.beginPath(); g.ellipse(ex + 3, 6, 4, 11, 0, 0, Math.PI * 2); g.fill();
      }
      break;
    }
  }
  g.restore();
  // A health bar once it's been hit.
  const def = BEASTS[b.kind];
  const max = b.max ?? def.hp;
  if (b.hp < max) {
    g.save();
    const w = b.kind === 'kraken' ? 140 : 44, y = b.y - def.r - (b.kind === 'kraken' ? 70 : 26);
    g.fillStyle = 'rgba(30,38,51,0.7)'; g.fillRect(b.x - w / 2 - 2, y - 2, w + 4, 8);
    g.fillStyle = '#ff5a6e'; g.fillRect(b.x - w / 2, y, (w * Math.max(0, b.hp)) / max, 4);
    g.restore();
  }
}

/** A sword or trident in the fish's mouth, mid-swing (swing 0–1) or held (swing < 0). */
export function drawWeapon(g: CanvasRenderingContext2D, x: number, y: number, facing: 1 | -1, swing: number, trident: boolean) {
  const angle = swing < 0 ? -0.5 : -1.6 + Math.sin(swing * Math.PI) * 2.4;
  g.save();
  g.translate(x + facing * 18, y + 4);
  g.scale(facing, 1);
  g.rotate(angle);
  g.lineCap = 'round'; g.lineJoin = 'round'; g.strokeStyle = INK;
  if (trident) {
    g.strokeStyle = '#c99b2e'; g.lineWidth = 4;
    g.beginPath(); g.moveTo(0, 0); g.lineTo(58, 0); g.stroke();
    g.strokeStyle = '#ffd23d'; g.lineWidth = 3.5;
    g.beginPath(); g.moveTo(56, -12); g.lineTo(56, 12); g.moveTo(56, -12); g.lineTo(70, -12); g.moveTo(56, 0); g.lineTo(74, 0); g.moveTo(56, 12); g.lineTo(70, 12); g.stroke();
  } else {
    g.fillStyle = '#7a5434'; g.lineWidth = 2;
    g.beginPath(); g.roundRect(0, -3, 14, 6, 2); g.fill(); g.stroke();
    g.fillStyle = '#ffd23d'; g.beginPath(); g.roundRect(13, -9, 5, 18, 2); g.fill(); g.stroke();
    g.fillStyle = '#e3eaf2';
    g.beginPath(); g.moveTo(18, -4); g.lineTo(54, -3); g.lineTo(62, 0); g.lineTo(54, 3); g.lineTo(18, 4); g.closePath(); g.fill(); g.stroke();
  }
  if (swing > 0.15 && swing < 0.85) {
    g.strokeStyle = 'rgba(255,255,255,0.55)'; g.lineWidth = 3;
    g.beginPath(); g.arc(0, 0, trident ? 74 : 62, -0.5, 0.5); g.stroke();
  }
  g.restore();
}
